import {
  CORE_PACKAGE_NAME,
  CORE_RUNTIME_PHASE,
  type Cell,
  type ColorNodePair,
  type CoreRuntimeInfo,
  type GridPoint,
  GridFlowCoreError,
  type LevelConfig,
  type LevelState,
  type LevelSnapshot,
  type NodeConfig,
  type OriginNodeConfig,
  type PathState,
  type TargetNodeConfig
} from "./types";

export function createCoreRuntimeInfo(): CoreRuntimeInfo {
  return Object.freeze({
    packageName: CORE_PACKAGE_NAME,
    phase: CORE_RUNTIME_PHASE
  });
}

export function createLevelState(config: LevelConfig): LevelState {
  validateLevelShape(config);

  const obstacles = new Set<string>();
  for (const obstacle of config.obstacles ?? []) {
    assertPointInBounds(config, obstacle, "obstacle");
    const key = pointKey(obstacle);
    if (obstacles.has(key)) {
      throw new GridFlowCoreError("DUPLICATE_OBSTACLE", `Duplicate obstacle at ${key}.`);
    }
    obstacles.add(key);
  }

  const nodesByColor = collectNodes(config, obstacles);
  const fillableCount = config.width * config.height - obstacles.size;
  validateTargetLengths(nodesByColor, fillableCount, config.fillRequired);

  const grid = buildGrid(config, obstacles, nodesByColor);
  const paths = new Map<string, PathState>();

  for (const [color, pair] of nodesByColor) {
    const path: PathState = {
      color,
      targetLength: pair.origin.targetLength,
      cells: [clonePoint(pair.origin.position)],
      isLinked: false
    };
    paths.set(color, path);
    occupyCell(grid, pair.origin.position, color, 0);
  }

  return {
    id: config.id,
    width: config.width,
    height: config.height,
    fillRequired: config.fillRequired,
    fillableCount,
    grid,
    paths,
    nodesByColor
  };
}

export function checkWinCondition(state: LevelState): boolean {
  for (const path of state.paths.values()) {
    if (!path.isLinked || path.cells.length !== path.targetLength) {
      return false;
    }
  }

  if (!state.fillRequired) {
    return true;
  }

  for (const row of state.grid) {
    for (const cell of row) {
      if (!cell.isObstacle && cell.occupiedByColor === null) {
        return false;
      }
    }
  }

  return true;
}

export function createLevelSnapshot(state: LevelState): LevelSnapshot {
  const grid = state.grid.map((row) =>
    row.map((cell) => ({
      x: cell.x,
      y: cell.y,
      isObstacle: cell.isObstacle,
      hasNode: cell.hasNode,
      nodeRole: cell.nodeRole,
      nodeColor: cell.nodeColor,
      occupiedByColor: cell.occupiedByColor,
      pathIndex: cell.pathIndex
    }))
  );

  const paths = Array.from(state.paths.values()).map((path) => ({
    color: path.color,
    targetLength: path.targetLength,
    currentLength: path.cells.length,
    remainingLength: path.targetLength - path.cells.length,
    isLinked: path.isLinked,
    isComplete: path.isLinked && path.cells.length === path.targetLength,
    cells: path.cells.map(clonePoint)
  }));

  return Object.freeze({
    id: state.id,
    width: state.width,
    height: state.height,
    fillRequired: state.fillRequired,
    fillableCount: state.fillableCount,
    occupiedCount: countOccupiedCells(state),
    hasWon: checkWinCondition(state),
    grid,
    paths
  });
}

export function getPathState(state: LevelState, color: string): PathState {
  const path = state.paths.get(color);
  if (!path) {
    throw new GridFlowCoreError("UNKNOWN_COLOR", `Unknown path color "${color}".`);
  }
  return path;
}

export function getNodePair(state: LevelState, color: string): ColorNodePair {
  const pair = state.nodesByColor.get(color);
  if (!pair) {
    throw new GridFlowCoreError("UNKNOWN_COLOR", `Unknown node color "${color}".`);
  }
  return pair;
}

export function getCell(state: LevelState, point: GridPoint): Cell {
  if (!isPointInBounds(state, point)) {
    throw new GridFlowCoreError(
      "OUT_OF_BOUNDS",
      `Point ${pointKey(point)} is outside ${state.width}x${state.height}.`
    );
  }

  const row = state.grid[point.y];
  const cell = row?.[point.x];
  if (!cell) {
    throw new GridFlowCoreError("CELL_NOT_FOUND", `Cell ${pointKey(point)} was not initialized.`);
  }
  return cell;
}

export function appendCellToPath(state: LevelState, color: string, point: GridPoint): void {
  const path = getPathState(state, color);
  const tail = getTail(path);
  const cell = getCell(state, point);

  if (!areAdjacent(tail, point)) {
    throw new GridFlowCoreError(
      "NON_ADJACENT_APPEND",
      `Cannot append ${pointKey(point)} after ${pointKey(tail)}.`
    );
  }

  if (cell.isObstacle) {
    throw new GridFlowCoreError("OBSTACLE_APPEND", `Cannot append obstacle ${pointKey(point)}.`);
  }

  if (cell.hasNode && cell.nodeColor !== color) {
    throw new GridFlowCoreError(
      "FOREIGN_NODE_APPEND",
      `Cannot append through ${cell.nodeColor ?? "foreign"} node ${pointKey(point)}.`
    );
  }

  if (cell.occupiedByColor !== null) {
    throw new GridFlowCoreError(
      cell.occupiedByColor === color ? "SELF_OVERLAP" : "OCCUPIED_BY_OTHER",
      `Cell ${pointKey(point)} is already occupied by ${cell.occupiedByColor}.`
    );
  }

  if (path.isLinked) {
    throw new GridFlowCoreError("APPEND_AFTER_TARGET", `Path ${color} already ends at its target.`);
  }

  path.cells.push(clonePoint(point));
  occupyCell(state.grid, point, color, path.cells.length - 1);
  refreshPathLinked(state, color);
}

export function popLastPathCell(state: LevelState, color: string): GridPoint {
  const path = getPathState(state, color);
  if (path.cells.length <= 1) {
    throw new GridFlowCoreError("CANNOT_POP_ORIGIN", `Cannot remove origin cell for ${color}.`);
  }

  const removed = path.cells.pop();
  if (!removed) {
    throw new GridFlowCoreError("EMPTY_PATH", `Path ${color} has no removable tail.`);
  }

  releaseCell(state.grid, removed, color);
  refreshPathIndexes(state, color);
  refreshPathLinked(state, color);
  return clonePoint(removed);
}

export function truncatePathFrom(
  state: LevelState,
  color: string,
  startIndex: number
): GridPoint[] {
  const path = getPathState(state, color);
  if (!Number.isInteger(startIndex) || startIndex < 1 || startIndex >= path.cells.length) {
    throw new GridFlowCoreError(
      "INVALID_TRUNCATE_INDEX",
      `Cannot truncate ${color} path from index ${startIndex}.`
    );
  }

  const removed = path.cells.slice(startIndex).map(clonePoint);
  for (const point of removed) {
    releaseCell(state.grid, point, color);
  }
  path.cells.splice(startIndex);
  refreshPathIndexes(state, color);
  refreshPathLinked(state, color);
  return removed;
}

export function restorePathCells(
  state: LevelState,
  color: string,
  cells: readonly GridPoint[]
): void {
  validatePathCellsForRestore(state, color, cells);
  replacePathCells(state, color, cells);
}

export function startPathAtNode(state: LevelState, color: string, point: GridPoint): GridPoint[] {
  const pair = getNodePair(state, color);
  if (!pointsEqual(point, pair.origin.position) && !pointsEqual(point, pair.target.position)) {
    throw new GridFlowCoreError(
      "START_POINT_NOT_NODE",
      `Path ${color} can only start at its origin or target.`
    );
  }

  const previous = getPathState(state, color).cells.map(clonePoint);
  replacePathCells(state, color, [point]);
  return previous;
}

export function clearPathToOrigin(state: LevelState, color: string): GridPoint[] {
  const path = getPathState(state, color);
  const previous = path.cells.map(clonePoint);
  const origin = getNodePair(state, color).origin.position;
  replacePathCells(state, color, [origin]);
  return previous;
}

export function findPathCellIndex(path: PathState, point: GridPoint): number {
  return path.cells.findIndex((cell) => pointsEqual(cell, point));
}

export function getTail(path: PathState): GridPoint {
  const tail = path.cells[path.cells.length - 1];
  if (!tail) {
    throw new GridFlowCoreError("EMPTY_PATH", `Path ${path.color} has no tail.`);
  }
  return tail;
}

export function getBacktrackPoint(path: PathState): GridPoint | null {
  return path.cells.length >= 2 ? clonePoint(path.cells[path.cells.length - 2] as GridPoint) : null;
}

export function pointsEqual(left: GridPoint, right: GridPoint): boolean {
  return left.x === right.x && left.y === right.y;
}

export function areAdjacent(left: GridPoint, right: GridPoint): boolean {
  const dx = Math.abs(left.x - right.x);
  const dy = Math.abs(left.y - right.y);
  return dx + dy === 1;
}

export function pointKey(point: GridPoint): string {
  return `${point.x},${point.y}`;
}

export function clonePoint(point: GridPoint): GridPoint {
  return { x: point.x, y: point.y };
}

function replacePathCells(state: LevelState, color: string, nextCells: readonly GridPoint[]): void {
  const path = getPathState(state, color);

  for (const point of path.cells) {
    releaseCell(state.grid, point, color);
  }

  path.cells = nextCells.map(clonePoint);
  path.cells.forEach((point, index) => {
    occupyCell(state.grid, point, color, index);
  });
  refreshPathLinked(state, color);
}

function refreshPathLinked(state: LevelState, color: string): void {
  const path = getPathState(state, color);
  const pair = getNodePair(state, color);
  const head = path.cells[0];
  const tail = getTail(path);

  path.isLinked =
    head !== undefined &&
    ((pointsEqual(head, pair.origin.position) && pointsEqual(tail, pair.target.position)) ||
      (pointsEqual(head, pair.target.position) && pointsEqual(tail, pair.origin.position)));
}

function refreshPathIndexes(state: LevelState, color: string): void {
  const path = getPathState(state, color);
  path.cells.forEach((point, index) => {
    const cell = getCell(state, point);
    if (cell.occupiedByColor === color) {
      cell.pathIndex = index;
    }
  });
}

function validatePathCellsForRestore(
  state: LevelState,
  color: string,
  cells: readonly GridPoint[]
): void {
  const pair = getNodePair(state, color);
  const first = cells[0];
  if (
    !first ||
    (!pointsEqual(first, pair.origin.position) && !pointsEqual(first, pair.target.position))
  ) {
    throw new GridFlowCoreError(
      "RESTORE_WITHOUT_ENDPOINT",
      `Restored ${color} path must start at origin or target.`
    );
  }

  const seen = new Set<string>();
  cells.forEach((point, index) => {
    const cell = getCell(state, point);
    if (cell.isObstacle) {
      throw new GridFlowCoreError("RESTORE_OBSTACLE", `Cannot restore ${color} through obstacle.`);
    }
    if (cell.hasNode && cell.nodeColor !== color) {
      throw new GridFlowCoreError(
        "RESTORE_FOREIGN_NODE",
        `Cannot restore ${color} through foreign node.`
      );
    }
    if (cell.occupiedByColor !== null && cell.occupiedByColor !== color) {
      throw new GridFlowCoreError(
        "RESTORE_OCCUPIED",
        `Cannot restore ${color}; ${pointKey(point)} is occupied by ${cell.occupiedByColor}.`
      );
    }
    if (seen.has(pointKey(point))) {
      throw new GridFlowCoreError(
        "RESTORE_SELF_OVERLAP",
        `Cannot restore ${color} with repeated cell.`
      );
    }
    seen.add(pointKey(point));
    if (index > 0) {
      const previous = cells[index - 1];
      if (!previous || !areAdjacent(previous, point)) {
        throw new GridFlowCoreError(
          "RESTORE_NON_ADJACENT",
          `Cannot restore non-adjacent ${color} path.`
        );
      }
    }
  });
}

function occupyCell(grid: Cell[][], point: GridPoint, color: string, pathIndex: number): void {
  const cell = grid[point.y]?.[point.x];
  if (!cell) {
    throw new GridFlowCoreError("CELL_NOT_FOUND", `Cell ${pointKey(point)} was not initialized.`);
  }
  cell.occupiedByColor = color;
  cell.pathIndex = pathIndex;
}

function releaseCell(grid: Cell[][], point: GridPoint, color: string): void {
  const cell = grid[point.y]?.[point.x];
  if (!cell) {
    throw new GridFlowCoreError("CELL_NOT_FOUND", `Cell ${pointKey(point)} was not initialized.`);
  }
  if (cell.occupiedByColor === color) {
    cell.occupiedByColor = null;
    cell.pathIndex = null;
  }
}

function buildGrid(
  config: LevelConfig,
  obstacles: ReadonlySet<string>,
  nodesByColor: ReadonlyMap<string, ColorNodePair>
): Cell[][] {
  const nodeByPoint = new Map<string, NodeConfig>();
  for (const pair of nodesByColor.values()) {
    nodeByPoint.set(pointKey(pair.origin.position), pair.origin);
    nodeByPoint.set(pointKey(pair.target.position), pair.target);
  }

  return Array.from({ length: config.height }, (_, y) =>
    Array.from({ length: config.width }, (_, x) => {
      const key = pointKey({ x, y });
      const node = nodeByPoint.get(key);
      return {
        x,
        y,
        isObstacle: obstacles.has(key),
        hasNode: node !== undefined,
        nodeRole: node?.role ?? null,
        nodeColor: node?.color ?? null,
        occupiedByColor: null,
        pathIndex: null
      };
    })
  );
}

function collectNodes(
  config: LevelConfig,
  obstacles: ReadonlySet<string>
): Map<string, ColorNodePair> {
  const partialByColor = new Map<
    string,
    {
      origin?: OriginNodeConfig;
      target?: TargetNodeConfig;
    }
  >();
  const occupiedNodePoints = new Set<string>();

  for (const node of config.nodes) {
    validateNode(config, node, obstacles, occupiedNodePoints);
    const partial = partialByColor.get(node.color) ?? {};
    if (node.role === "origin") {
      if (partial.origin) {
        throw new GridFlowCoreError(
          "DUPLICATE_ORIGIN",
          `Color ${node.color} has multiple origins.`
        );
      }
      partial.origin = node;
    } else {
      if (partial.target) {
        throw new GridFlowCoreError(
          "DUPLICATE_TARGET",
          `Color ${node.color} has multiple targets.`
        );
      }
      partial.target = node;
    }
    partialByColor.set(node.color, partial);
  }

  const nodesByColor = new Map<string, ColorNodePair>();
  for (const [color, partial] of partialByColor) {
    if (!partial.origin || !partial.target) {
      throw new GridFlowCoreError(
        "INCOMPLETE_NODE_PAIR",
        `Color ${color} must have one origin and one target.`
      );
    }
    nodesByColor.set(color, {
      origin: partial.origin,
      target: partial.target
    });
  }

  if (nodesByColor.size === 0) {
    throw new GridFlowCoreError("NO_NODES", "A level must contain at least one color pair.");
  }

  return nodesByColor;
}

function validateNode(
  config: LevelConfig,
  node: NodeConfig,
  obstacles: ReadonlySet<string>,
  occupiedNodePoints: Set<string>
): void {
  if (node.color.trim().length === 0) {
    throw new GridFlowCoreError("EMPTY_COLOR", "Node color cannot be empty.");
  }

  assertPointInBounds(config, node.position, `${node.color} ${node.role}`);
  const key = pointKey(node.position);
  if (obstacles.has(key)) {
    throw new GridFlowCoreError(
      "NODE_ON_OBSTACLE",
      `Node ${node.color} ${node.role} is on obstacle ${key}.`
    );
  }
  if (occupiedNodePoints.has(key)) {
    throw new GridFlowCoreError("DUPLICATE_NODE_POSITION", `Multiple nodes occupy ${key}.`);
  }
  occupiedNodePoints.add(key);

  if (node.role === "origin") {
    assertPositiveInteger(node.targetLength, `${node.color} targetLength`);
  }
}

function validateTargetLengths(
  nodesByColor: ReadonlyMap<string, ColorNodePair>,
  fillableCount: number,
  fillRequired: boolean
): void {
  let totalTargetLength = 0;
  for (const [color, pair] of nodesByColor) {
    if (pair.origin.targetLength > fillableCount) {
      throw new GridFlowCoreError(
        "TARGET_LENGTH_TOO_LONG",
        `Target length for ${color} exceeds fillable cell count.`
      );
    }
    totalTargetLength += pair.origin.targetLength;
  }

  if (fillRequired && totalTargetLength !== fillableCount) {
    throw new GridFlowCoreError(
      "FILL_REQUIRED_LENGTH_MISMATCH",
      `fillRequired levels need target lengths ${totalTargetLength} to equal fillable cells ${fillableCount}.`
    );
  }
}

function validateLevelShape(config: LevelConfig): void {
  if (config.id.trim().length === 0) {
    throw new GridFlowCoreError("EMPTY_LEVEL_ID", "Level id cannot be empty.");
  }
  assertPositiveInteger(config.width, "width");
  assertPositiveInteger(config.height, "height");
}

function assertPointInBounds(
  config: Pick<LevelConfig, "width" | "height">,
  point: GridPoint,
  label: string
): void {
  assertInteger(point.x, `${label}.x`);
  assertInteger(point.y, `${label}.y`);
  if (point.x < 0 || point.x >= config.width || point.y < 0 || point.y >= config.height) {
    throw new GridFlowCoreError(
      "POINT_OUT_OF_BOUNDS",
      `${label} point ${pointKey(point)} is outside ${config.width}x${config.height}.`
    );
  }
}

function assertPositiveInteger(value: number, label: string): void {
  assertInteger(value, label);
  if (value <= 0) {
    throw new GridFlowCoreError("NOT_POSITIVE_INTEGER", `${label} must be a positive integer.`);
  }
}

function assertInteger(value: number, label: string): void {
  if (!Number.isInteger(value)) {
    throw new GridFlowCoreError("NOT_INTEGER", `${label} must be an integer.`);
  }
}

function isPointInBounds(state: Pick<LevelState, "width" | "height">, point: GridPoint): boolean {
  return point.x >= 0 && point.x < state.width && point.y >= 0 && point.y < state.height;
}

function countOccupiedCells(state: LevelState): number {
  let count = 0;
  for (const row of state.grid) {
    for (const cell of row) {
      if (cell.occupiedByColor !== null) {
        count += 1;
      }
    }
  }
  return count;
}
